import React, { useState } from "react";
import type { ParentFolder } from "types/qbank/folder";
import { useFilter } from "hooks/filter";
import { useApi } from "hooks/api";
import { useQuery } from "react-query";
import FolderList from "./FolderList";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import FolderBreadcrumb from "./FolderBreadcrumb";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Chip, IconButton, Skeleton, styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import ErrorIcon from "@mui/icons-material/Error";

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  return {
    height: theme.spacing(3),
  };
}) as typeof Chip;

export default function FolderBrowser() {
  const { getFolder } = useApi();
  const { searchState } = useFilter();

  const {
    isLoading,
    isError,
    error,
    data: currentFolder,
  } = useQuery(
    ["getFolder", searchState.currentFolderId],
    () => getFolder(searchState.currentFolderId),
    {
      retry: 0,
      staleTime: Infinity,
    }
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
      return;
    }
    setAnchorEl(null);
  };

  if (searchState.currentFolderId === 0) {
    return null;
  }

  if (isError) {
    return (
      <Grid container>
        <Breadcrumbs aria-label="breadcrumb">
          <StyledBreadcrumb
            color="warning"
            // @ts-ignore
            label={error.message}
            icon={<ErrorIcon fontSize="small" />}
          />
        </Breadcrumbs>
      </Grid>
    );
  }

  if (isLoading || currentFolder === undefined) {
    return (
      <Grid container justifyContent="center">
        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
        <Skeleton variant="rectangular" width={100} />
      </Grid>
    );
  }

  return (
    <Grid container>
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={
          currentFolder.subFolders.length === 0 &&
          searchState.currentFolderId ===
            searchState.currentCategory.rootFolderId
        }
      >
        <MoreHorizIcon fontSize="inherit" />
      </IconButton>
      <FolderList
        folder={currentFolder}
        handleClose={handleClose}
        open={open}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
      />
      <Breadcrumbs maxItems={3} aria-label="breadcrumb">
        {currentFolder.parents?.map((parent: ParentFolder) => (
          <FolderBreadcrumb
            folder={parent.folder}
            key={parent.folder.id}
            disabled={false}
          />
        ))}
        <FolderBreadcrumb
          folder={currentFolder}
          disabled={currentFolder.subFolders.length === 0}
        />
      </Breadcrumbs>
    </Grid>
  );
}
